import { useEffect } from 'react';
import { X, Bell } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useUnreadNotifications, useMarkRead, type InboxItem } from './api';

/**
 * Persistent "sintillo llamativo" banner under the TopBar.
 *
 * - Renders the most recent unread notification; if there are more, shows a
 *   `+N más` chip that anchors the user toward the Bell dropdown.
 * - Animated accent border (pulse) to draw the eye without screaming.
 * - X marks-as-read via the inapp source. ESC also dismisses.
 */
export function NotificationBanner() {
  const t = useT();
  const { data: items } = useUnreadNotifications();
  const markRead = useMarkRead();

  const list: InboxItem[] = items ?? [];
  const current = list[0];

  useEffect(() => {
    if (!current) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        markRead.mutate({ id: current.notificationId, source: 'inapp' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, markRead]);

  if (!current) return null;

  const sender = current.notification.sender;
  const senderName =
    `${sender?.firstname ?? ''} ${sender?.lastname ?? ''}`.trim() ||
    sender?.email ||
    t('notifications.banner.systemSender');
  const initial = (sender?.firstname?.[0] ?? sender?.email?.[0] ?? '?').toUpperCase();
  const more = list.length - 1;

  return (
    <div className="sticky top-14 z-20 border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-8 py-2">
      <div
        className={cn(
          'relative flex items-center gap-3 overflow-hidden rounded-xl border px-4 py-3',
          'border-[var(--color-xk-accent)] bg-[color-mix(in_srgb,var(--color-xk-accent)_10%,transparent)]',
          'animate-[notification-pulse_2.4s_ease-in-out_infinite] shadow-[0_0_0_1px_rgba(178,98,245,0.18),0_8px_24px_-12px_rgba(178,98,245,0.5)]',
        )}
        style={{ ['--tw-shadow-color' as never]: 'var(--color-xk-accent)' } as React.CSSProperties}
      >
        {/* Sender avatar */}
        <span
          aria-hidden="true"
          className="grid h-9 w-9 flex-none place-items-center rounded-full bg-[var(--color-xk-accent)] text-[12px] font-extrabold text-[#0A0A0A]"
        >
          {initial}
        </span>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Bell className="h-3 w-3 flex-none text-[var(--color-xk-accent)]" />
            <span
              className="truncate text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {senderName}
            </span>
            <span className="text-[11px] text-[var(--color-xk-text-muted)]">·</span>
            <span className="truncate text-[12px] font-extrabold text-[var(--color-xk-lavender)]">
              {current.notification.title}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-1 whitespace-pre-line text-[12px] text-[var(--color-xk-lavender)]/90">
            {current.notification.body}
          </p>
        </div>

        {/* +N más chip */}
        {more > 0 && (
          <span
            className="flex-none rounded-full border border-[var(--color-xk-accent)] bg-[color-mix(in_srgb,var(--color-xk-accent)_16%,transparent)] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            +{more} {t('notifications.banner.moreCount')}
          </span>
        )}

        {/* X mark-read */}
        <button
          type="button"
          onClick={() =>
            markRead.mutate({ id: current.notificationId, source: 'inapp' })
          }
          disabled={markRead.isPending}
          title={t('notifications.banner.markRead')}
          aria-label={t('notifications.banner.markRead')}
          className="grid h-7 w-7 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* keyframes injected inline so we don't have to touch the Tailwind config */}
      <style>{`
        @keyframes notification-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(178,98,245,0.18), 0 8px 24px -12px rgba(178,98,245,0.5); }
          50%      { box-shadow: 0 0 0 2px rgba(178,98,245,0.45), 0 12px 32px -10px rgba(178,98,245,0.7); }
        }
      `}</style>
    </div>
  );
}
